import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router";
import { toast } from "sonner";

import { useAppNavigate } from "Infrastructure/Routing/useAppNavigate";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { useResolveRegistrationRequestMutation } from "RegistrationRequests/Api/Mutations/useResolveRegistrationRequestMutation";
import { useRegistrationRequestDetailQuery } from "RegistrationRequests/Api/Queries/useRegistrationRequestDetailQuery";
import {
  RegistrationRequestStatus,
  RegistrationRequestType,
} from "RegistrationRequests/Api/registrationRequestApi";
import { RegistrationRequestStatusChip } from "RegistrationRequests/Components/RegistrationRequestStatusChip";
import { RegistrationRequestTypeChip } from "RegistrationRequests/Components/RegistrationRequestTypeChip";
import { BackButton } from "Shared/Buttons/BackButton";
import { AuthenticatedLayout } from "Shared/Layouts/AuthenticatedLayout";
import { DateFormat, formatDate } from "Shared/Utils/Format/date";

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
}

const DetailRow = ({ label, value }: DetailRowProps): React.ReactNode => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: 1.5,
      borderBottom: "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {value}
    </Typography>
  </Box>
);

export const RegistrationRequestDetailPage = (): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();
  const navigate = useAppNavigate();
  const { registrationRequestId } = useParams<{
    registrationRequestId: string;
  }>();

  const { data: detail, isLoading } = useRegistrationRequestDetailQuery(
    registrationRequestId,
  );
  const resolveMutation = useResolveRegistrationRequestMutation();

  const handleResolve = async () => {
    if (!registrationRequestId) return;

    try {
      await resolveMutation.mutateAsync(registrationRequestId);
      toast.success(t(Resources.RegistrationRequests.ResolveSuccess));
    } catch {
      // auto-toast shown by hook
    }
  };

  const handleCreatePartner = () => {
    if (!detail) return;

    navigate(AppRouting.Partners.path(), {
      state: {
        prefillPartner: {
          companyName: detail.company ?? "",
          email: detail.email,
          phone: detail.phone,
        },
      },
    });
  };

  if (isLoading) {
    return (
      <AuthenticatedLayout title={t(Resources.Common.RegistrationRequests)}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <CircularProgress />
        </Box>
      </AuthenticatedLayout>
    );
  }

  if (!detail) {
    return (
      <AuthenticatedLayout title={t(Resources.Common.RegistrationRequests)}>
        <Typography>{t(Resources.RegistrationRequests.NotFound)}</Typography>
      </AuthenticatedLayout>
    );
  }

  const isPending = detail.status === RegistrationRequestStatus.Pending;
  const isPartner = detail.type === RegistrationRequestType.Partner;

  return (
    <AuthenticatedLayout
      preTitle={
        <BackButton
          defaultText={t(Resources.Common.BackToRegistrationRequests)}
          defaultPath={AppRouting.RegistrationRequests.path()}
        />
      }
      title={`${detail.firstName} ${detail.lastName}`}
      titleEndAdornment={
        <Stack direction="row" spacing={Spacing.S}>
          <RegistrationRequestTypeChip type={detail.type} />
          <RegistrationRequestStatusChip status={detail.status} />
        </Stack>
      }
      action={
        isPending ? (
          <Stack direction="row" spacing={Spacing.S}>
            {isPartner && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreatePartner}
              >
                {t(Resources.RegistrationRequests.CreatePartner)}
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={handleResolve}
              disabled={resolveMutation.isPending}
            >
              {t(Resources.RegistrationRequests.Resolve)}
            </Button>
          </Stack>
        ) : undefined
      }
    >
      <Paper
        sx={{
          p: Spacing.L,
          backgroundColor: theme.colors.backgroundTile,
          borderRadius: Radius.M,
          maxWidth: 600,
        }}
      >
        <DetailRow
          label={t(Resources.RegistrationRequests.FirstName)}
          value={detail.firstName}
        />
        <DetailRow
          label={t(Resources.RegistrationRequests.LastName)}
          value={detail.lastName}
        />
        <DetailRow
          label={t(Resources.RegistrationRequests.Company)}
          value={detail.company ?? "-"}
        />
        <DetailRow
          label={t(Resources.RegistrationRequests.Email)}
          value={detail.email}
        />
        <DetailRow
          label={t(Resources.RegistrationRequests.Phone)}
          value={detail.phone}
        />
        <DetailRow
          label={t(Resources.RegistrationRequests.Type)}
          value={<RegistrationRequestTypeChip type={detail.type} />}
        />
        <DetailRow
          label={t(Resources.RegistrationRequests.Status)}
          value={<RegistrationRequestStatusChip status={detail.status} />}
        />
        <DetailRow
          label={t(Resources.RegistrationRequests.DateCreated)}
          value={formatDate(detail.dateCreated, DateFormat.DateTime)}
        />
      </Paper>
    </AuthenticatedLayout>
  );
};
